import { t } from '@hefe/i18n';
import { ResponseBasketballGetGameFormData } from '@hefe/models';
import {
  BasketballGameActions,
  BasketballGameSaveRequest,
  BasketballGamesSlice,
  BasketballTeamsSlice,
  GymsSlice,
  ModalsSlice,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm, {
  FormField,
} from '../../../core/components/form/custom-form';

interface AddEditTeamGameFormProps {
  gameId?: number;
}

export default function AddEditTeamGameForm(props: AddEditTeamGameFormProps) {
  const { gameId } = props;

  const dispatch = useAppDispatch();

  const [ownClubApponent, setOwnClubApponent] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [requestData, setRequestData] =
    useState<ResponseBasketballGetGameFormData>();
  const [isHomeGame, setHomeGame] = useState(requestData?.home_game ?? false);

  const [loadingRequestData, setLoadingRequestData] = useState(false);

  const teamId = useSelector(userActiveBasketballCoachTeamId);

  const gyms = useSelector(GymsSlice.gymsSelectSelector);
  const teams = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const formKey = `add-edit-team-game-form-${gameId ?? 'new'}`;

  useEffect(() => {
    if (gameId) {
      setLoadingRequestData(true);
      dispatch(BasketballGamesSlice.fetchBasketballGameFormData(gameId))
        .unwrap()
        .then((data) => {
          setRequestData(data);
          setHomeGame(data.home_game ?? false);
        })
        .finally(() => setLoadingRequestData(false));
    }
  }, [gameId]);

  const onSubmit = (values: any, resetForm: any) => {
    const setLoading = ModalsSlice.modalsActions.setModalLoading;
    const close = ModalsSlice.modalsActions.closeModal;
    let gym = values.gym;

    if (isHomeGame) {
      const gymSelect = gyms.find((g) => g.value === values.gym);
      if (gymSelect) {
        gym = gymSelect.label;
      }
    }

    let home = undefined;
    let homeTeamId = undefined;
    let guest = undefined;
    let guestTeamId = undefined;

    if (ownClubApponent) {
      homeTeamId = teamId;
      guestTeamId = values.guest_team_id;
    } else {
      if (isHomeGame) {
        homeTeamId = teamId;
        guest = values.opponent;
      } else {
        home = values.opponent;
        guestTeamId = teamId;
      }
    }

    const submitData: BasketballGameSaveRequest = {
      ...(requestData ? { id: requestData.id } : {}),
      ...values,
      date: dayjs(values.date).format('YYYY-MM-DD HH:mm:ss'),
      gym: gym,
      own_home_team: isHomeGame || ownClubApponent || false,
      own_guest_team: !isHomeGame || ownClubApponent || false,
      home: home,
      home_team_id: homeTeamId,
      guest: guest,
      guest_team_id: guestTeamId,
      is_home: values.is_home || false,
      canceled: values.canceled || false,
      own_referees: values.own_referees || false,
      return_full_game: true,
    };

    dispatch(setLoading(true));
    dispatch(BasketballGameActions.saveBasketballGame(submitData))
      .unwrap()
      .then(() => {
        dispatch(close());
        setRefresh((old) => !old);
      })
      .catch(() => null)
      .finally(() => dispatch(setLoading(false)));
  };

  const opponentField = (): FormField => {
    if (!ownClubApponent) {
      return {
        name: 'opponent',
        label: t('opponent'),
        type: 'text',
        initalValue: isHomeGame ? requestData?.guest : requestData?.home,
        rules: [
          {
            required: true,
            message: t('form_errors.name'),
          },
        ],
      };
    } else {
      return {
        name: 'guest_team_id',
        label: t('opponent'),
        type: 'select',
        initalValue: isHomeGame
          ? requestData?.guest_team_id
          : requestData?.home_team_id,
        selectOptions: teams.filter((t) => t.value !== teamId),
        rules: [
          {
            required: true,
            message: t('form_errors.select_value'),
          },
        ],
      };
    }
  };

  const gymField = (): FormField => {
    if (!isHomeGame) {
      return {
        name: 'gym',
        label: t('gyms.singular'),
        type: 'text',
        initalValue: requestData?.gym,
        rules: [
          {
            required: true,
            message: t('form_errors.name'),
          },
        ],
      };
    } else {
      return {
        name: 'gym',
        label: t('gyms.singular'),
        type: 'select',
        initalValue: gyms.find((g) => g.label === requestData?.gym)?.value,
        selectOptions: gyms,
        rules: [
          {
            required: true,
            message: t('form_errors.select_value'),
          },
        ],
      };
    }
  };

  if (loadingRequestData) {
    return <Spin />;
  }

  return (
    <CustomForm
      key={formKey}
      formId={formKey}
      refresh={refresh}
      fields={[
        {
          name: 'date',
          label: t('date'),
          type: 'datetime',
          initalValue: requestData?.date,
          rules: [
            {
              required: true,
              message: t('form_errors.date'),
            },
          ],
        },
        {
          name: 'own_club_apponent',
          label: t('games.form.own_club_apponent'),
          type: 'checkbox',
          initalValue: ownClubApponent,
          value: ownClubApponent,
          onChange: (value) => setOwnClubApponent(value),
          rules: [],
        },
        { ...opponentField() },
        {
          name: 'is_home',
          label: t('home_game'),
          type: 'checkbox',
          initalValue: isHomeGame,
          value: isHomeGame,
          onChange: (value) => setHomeGame(value),
          rules: [],
        },
        { ...gymField() },
        {
          name: 'own_referees',
          label: t('own_referees'),
          type: 'checkbox',
          initalValue: requestData?.own_referees,
          rules: [],
        },
        {
          name: 'canceled',
          label: t('canceled'),
          type: 'checkbox',
          initalValue: requestData?.canceled,
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
